import { Component, Inject, OnDestroy } from "@angular/core";
import { CheckboxComponent } from "@tylertech/forge";
import { PopoverRef } from "@tylertech/forge-angular";
import { ProfileSettingViewModel, SettingLookupOption } from "@tylertech/tyler-api-types";
import { LOCATION, WINDOW } from "@tylertech/shared-module";
import { BrowserFontSize, BrowserService, ConnectionSettings, isHttpResponseError, SecurityService, ToastService, UserProfileService } from "../../../services";
import { ConnGroupSettings, ConnGroupStorageKey } from "./model";

@Component({
    selector: "conn-group-popup",
    templateUrl: "./connGroupPopover.tmpl.html",
    styleUrls: [
        "./connGroupPopover.component.scss"
    ],
    standalone: false
})
export class ConnGroupPopoverComponent
    implements OnDestroy
{
    updateConnGroupDefault: boolean = false;
    connGroupIdToUpdate: string;
    departmentIdToUpdate: string;
    PrevConnGroupID: string = "";
    connGroupDisabled: boolean = false;
    departmentDisabled: boolean = false;

    saveClicked: boolean = false;

    constructor(
        private readonly popoverRef: PopoverRef,
        @Inject(WINDOW) private readonly window: globalThis.Window,
        @Inject(LOCATION) private readonly location: Location,
        private readonly $toastService: ToastService,
        private readonly $securityService: SecurityService,
        private readonly $userProfileService: UserProfileService,
        private readonly $browserService: BrowserService
    )
    { }

    get ConnGroupList(): Array<SettingLookupOption>
    {

        if (!this.$securityService.hasSetting(ConnGroupSettings.ConnGroupList))
        {
            return null;
        }

        return this.$securityService.Settings.ConnGroupList as Array<SettingLookupOption>;
    }

    get ConnGroup(): string
    {
        return this.ConnGroupList.find(x => x.Value === this.ConnGroupID)?.Display ?? "";
    }

    get ConnGroupID(): string
    {
        return this.window.sessionStorage.getItem(ConnGroupStorageKey.ConnGroupId);
    }

    set ConnGroupID(value: string)
    {
        if (!value)
        {
            return;
        }

        this.window.sessionStorage.setItem(ConnGroupStorageKey.ConnGroupId, value);
    }

    get DepartmentList(): Array<SettingLookupOption>
    {
        if (!this.$securityService.hasSetting(ConnGroupSettings.DepartmentList))
        {
            return null;
        }

        const departmentList = this.$securityService.Settings[ConnGroupSettings.DepartmentList] as Array<SettingLookupOption>;
        return departmentList.sort((a, b) => a.Display.localeCompare(b.Display));
    }

    get Department(): string
    {
        if (!this.$securityService.hasSetting(ConnectionSettings.Department))
        {
            return null;
        }

        const departmentSetting = this.$securityService.Settings[ConnectionSettings.Department] as SettingLookupOption;
        return departmentSetting.Display;
    }

    get DepartmentID(): string
    {
        if (!this.$securityService.hasSetting(ConnectionSettings.Department))
        {
            return null;
        }

        const departmentSetting = this.$securityService.Settings[ConnectionSettings.Department] as SettingLookupOption;
        return departmentSetting.Value;
    }

    get canSave(): boolean
    {
        return !!this.connGroupIdToUpdate
            || !!this.departmentIdToUpdate
            || this.updateConnGroupDefault;
    }

    get density(): string
    {
        const defaultDensity = "small";
        let density = defaultDensity;

        const browserFontSize = this.$browserService.browserFontSetting
            ;

        if (browserFontSize > BrowserFontSize.Medium && browserFontSize < BrowserFontSize.VeryLarge)
        {
            density = "large";
        }
        else if (browserFontSize >= BrowserFontSize.VeryLarge)
        {
            density = "extra-large";
        }

        return density;
    }

    updateConnGroup(connGroupId: string): void
    {
        this.departmentDisabled = true;
        this.PrevConnGroupID = this.ConnGroupID;
        this.ConnGroupID = connGroupId;
        this.connGroupIdToUpdate = connGroupId;
    }

    updateDepartment(departmentID: string): void
    {
        this.connGroupDisabled = true;
        this.departmentIdToUpdate = departmentID;
        this.save();
    }

    checked($event: Event): void
    {
        const eventTarget = $event.currentTarget as CheckboxComponent;
        this.updateConnGroupDefault = eventTarget.checked;
        if (this.updateConnGroupDefault)
        {
            this.departmentDisabled = true;
        }
        else if (!this.connGroupIdToUpdate)
        {
            this.departmentDisabled = false;
        }
    }

    selectKeyUp(event: KeyboardEvent): void
    {
        if (event.key === "Escape")
        {
            this.popoverRef.close();
        }
    }

    ngOnDestroy(): void
    {
        if (this.canSave && !this.saveClicked)
            this.save();
    }

    save(): Promise<void>
    {
        this.saveClicked = true;

        const settingsToUpdate: Array<ProfileSettingViewModel> = [];

        if (this.updateConnGroupDefault && !this.connGroupIdToUpdate)
        {
            this.connGroupIdToUpdate = this.ConnGroupID;
        }

        if (!this.connGroupDisabled && this.connGroupIdToUpdate)
        {
            settingsToUpdate.push({
                Id: "ConnGroupId",
                Value: this.connGroupIdToUpdate,
            });
        }

        if (!this.departmentDisabled && this.departmentIdToUpdate)
        {
            settingsToUpdate.push({
                Id: "DacId",
                Value: this.departmentIdToUpdate,
            });
        }

        settingsToUpdate.push({
            Id: "UpdateConnGroup",
            Value: this.updateConnGroupDefault,
        });

        return this.$userProfileService.Save({
            Settings: settingsToUpdate,
            Type: undefined,
        })
            .then(
                () =>
                {
                    this.location.reload();
                },
                (error: unknown) =>
                {
                    let msg;
                    if (typeof error === "string")
                    {
                        msg = error;
                    }
                    else if (isHttpResponseError(error))
                    {
                        msg = error.error.Message;
                        if (error.error.VerificationId === "UnconvertedConnGroup")
                        {
                            this.ConnGroupID = this.PrevConnGroupID;
                        }
                    }

                    this.connGroupIdToUpdate = null;
                    this.departmentIdToUpdate = null;
                    this.updateConnGroupDefault = false;
                    this.departmentDisabled = false;
                    this.connGroupDisabled = false;
                    this.$toastService.showToast(msg);
                });
    }

    cancel(): void
    {
        this.connGroupIdToUpdate = null;
        this.departmentIdToUpdate = null;
        this.updateConnGroupDefault = false;
        this.departmentDisabled = false;
        this.connGroupDisabled = false;
        this.popoverRef.close();
    }
}
